import './Share.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { CopyLink } from '../CopyLink/CopyLink';
import { Header } from '../Header/Header';
import {
  ISharingInformation, ISharingLinkSettings, ISharingLink, ISharingStore,
  ClientId, ShareType, SharingAudience, Mode, IEngagementExtraData, SharingRole, SharingLinkKind,
  IPolicyTipInformation, AccessStatus, SharePermission, OutlookAvailability, ISharingItemInformation,
  ICustomLabeledAudienceConfig
} from '../../interfaces/SharingInterfaces';
import { ModifyPermissions } from '../ModifyPermissions/ModifyPermissions';
import { PermissionsList } from '../PermissionsList/PermissionsList';
import { ShareMain } from '../ShareMain/ShareMain';
import { ShareNotification } from '../ShareNotification/ShareNotification';
import { SharePolicyDetails } from '../SharePolicyDetails/SharePolicyDetails';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import * as EngagementHelper from '../../utilities/EngagementHelper';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as PeoplePickerHelper from '../../utilities/PeoplePickerHelper';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import { Fabric } from 'office-ui-fabric-react/lib/Fabric';
import { ActivityIndicator } from '../ActivityIndicator/ActivityIndicator';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { AtMention } from '../AtMention/AtMention';
import { Notify } from '../Notify/Notify';
import { getDarkTheme } from '../../utilities/ShareHelper';
import Uri from '@ms/odsp-utilities/lib/uri/Uri';
import stringsResx from '../Share.resx';
import * as SharingAbilitiesHelper from '../../utilities/SharingAbilitiesHelper';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import EventGroup from '@ms/odsp-utilities/lib/events/EventGroup';
import { AddPeople } from '../AddPeople/AddPeople';
import { loadTheme, IPartialTheme } from 'office-ui-fabric-react/lib/Styling';
import { getLinkParticipants, isCustomLabeledSpecificPeopleSupported } from '../../utilities/ShareHelper';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';

export interface IShareProps {
  clientId?: ClientId; // Identifier of which partner is hosting.
  scenarioId?: string;
  copyLinkShortcut?: boolean; // If true, bypass share UI and create the default sharing link.
  showExistingAccessOption?: boolean;
  onManageAccessClicked?: () => void; // Callback for when user clicks on "Manage Access".
  hideShareDialogOverflowButton?: boolean; // If true, hide overflow button form share UI
  createPeopleSharingLinks?: boolean; // Determines if "Specific people" creates PSLs or adds to the ACL.
  maxHeight?: number;
  mode?: Mode;
  shareId?: string;
  theme?: IPartialTheme;
  enableShimmer?: boolean;
  sharingItemInformation?: ISharingItemInformation;
  messageText?: string; // default value for the message field
  subjectText?: string; // default value for the subject field
  customLabeledAudienceConfig?: ICustomLabeledAudienceConfig;
}

export interface IShareState {
  companyName: string;
  currentSettings: ISharingLinkSettings; // Defines the currently selected link settings.
  shareType: ShareType;
  sharingInformation: ISharingInformation; // Object that has all of the relevant sharing information to render UI.
  sharingLinkCreated: ISharingLink; // The link that is created from the UI.
  viewState: ShareViewState;
  readyToCopy: boolean;
  recipientsCount?: number; // Used for telemetry only.
  hasMessage?: boolean; // Used for telemetry only.
  hasSubject?: boolean; // Used for telemetry only.
  messageLength?: number; // Used for telemetry only.
  externalRecipientsCount?: number; // Used for telemetry only.
  linkedinRecipientsCount?: number; // Used for telemetry only.
  groupsMemberCount: number;
  policyTipInformation: IPolicyTipInformation;
  linkRecipients: Array<IPerson>; // List of recipients that'll receive email with link (not necessarily permissioned).
  shareTargetClicked: boolean;
  applyLinkSettingsClicked: boolean;
  showActivityIndicator: boolean;
  permissionsMap: { [index: string]: AccessStatus };
  messageText: string; // The text currently in the message field.
  subjectText: string;
  isLoadingDefaultRecipients: boolean;
  isCloseButtonOnFocus: boolean; // Fix Android talkback doesn't read onfocus element.
}

export const enum ShareViewState {
  default,
  shareShimmer,
  modifyPermissions,
  linkSuccess,
  permissionsList,
  grantPermissions,
  policyDetails,
  error,
  linkSettings,
  notify,
  addPeople,
  addPeopleRequestAccess
}

declare const window: Window & {
  msCrypto?: Crypto
};

export class Share extends React.Component<IShareProps, IShareState> {
  private _dismiss: () => void;
  private _engagementExtraData: IEngagementExtraData;
  private _resize: () => void;
  private _store: ISharingStore;
  private _viewStates: Array<number> = [];
  private _outlookAvailability: OutlookAvailability;
  private _modifiedSharingLinkUrl: string;
  private _copyLinkShortcutInvoked: boolean;
  private _platformDetection: PlatformDetection;

  static contextTypes = {
    resize: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    sharingStore: PropTypes.object.isRequired,
    usesSharingAbilities: PropTypes.bool.isRequired,
    breakpoint: PropTypes.object.isRequired,
    platformDetection: PropTypes.object.isRequired
  };

  constructor(props: IShareProps, context: any) {
    super(props);

    this._dismiss = context.onDismiss;
    this._resize = context.resize;
    this._store = context.sharingStore;
    this._platformDetection = context.platformDetection;

    let isDarkMode = ShareCommunicationManager.isDarkMode();
    if (props.theme || isDarkMode) {
      this._loadTheme(isDarkMode ? getDarkTheme() : props.theme);
    }

    this.state = {
      companyName: null,
      currentSettings: null,
      sharingInformation: null,
      sharingLinkCreated: null,
      viewState: ShareViewState.default,
      readyToCopy: false,
      shareType: ShareType.share,
      groupsMemberCount: 0,
      policyTipInformation: null,
      linkRecipients: [],
      shareTargetClicked: false,
      applyLinkSettingsClicked: false,
      showActivityIndicator: false,
      permissionsMap: {},
      messageText: props.messageText || '',
      subjectText: props.subjectText || '',
      isLoadingDefaultRecipients: this._store.isValidatingDefaultRecipients(),
      isCloseButtonOnFocus: false
    };

    this._engagementExtraData = {
      clientId: props.clientId,
      scenarioId: props.scenarioId,
      mode: props.mode || (props.copyLinkShortcut ? Mode.copy : Mode.share)
    };
  }

  public componentDidMount() {
    EngagementHelper.opened(this._engagementExtraData);
    const store = this._store;

    /**
     * Add event listener to dismiss the share dialog when "Escape"
     * is pressed. If the user is on the modify permissions page, navigate
     * the dialog back to the main share view.
     */
    const eventGroup = new EventGroup(this);
    eventGroup.on(window, 'keydown', (event: KeyboardEvent) => {
      if (event && event.keyCode === 27) {
        this._handleEscKeyPress(event);
      }
    });

    // Subscribe to store change events.
    store.addListener(() => {
      // Get new information from store.
      const sharingInformation = store.getSharingInformation();
      const sharingLinkCreated = store.getSharingLinkCreated();
      const companyName = store.getCompanyName();
      const groupsMemberCount = store.getGroupsMemberCount();
      const policyTipInformation = store.getPolicyTipInformation();
      const permissionsMap = store.getPermissionsMap();
      const defaultRecipientsResponse = store.getDefaultRecipients();
      this._outlookAvailability = store.getOutlookAvailability();

      // If sharingInformation hasn't been set in the store, don't progress.
      if (!sharingInformation) {
        return;
      }

      if (sharingInformation.webTemplateId) {
        this._engagementExtraData.webTemplateId = sharingInformation.webTemplateId;
      }

      // If currentSettings haven't been initialized, initialize it
      // with sharingInformation.
      let settings = this.state.currentSettings;
      const hasSharingInformationError = sharingInformation && sharingInformation.error;
      const hasSharingLinks = sharingInformation.sharingLinks && sharingInformation.sharingLinks.length && this.props.shareId;

      if (!settings && !hasSharingInformationError) {
        let selectedLink = sharingInformation.defaultSharingLink;

        if (hasSharingLinks) {
          const matchingLink = sharingInformation.sharingLinks.filter((link) => {
            return link.shareId === this.props.shareId;
          })[0];
          if (matchingLink) {
            selectedLink = matchingLink;
          }
        }

        settings = this._initializeCurrentSettings(selectedLink, sharingInformation, this.props.copyLinkShortcut);
      }

      // This is breaking the linkSetting + manageAccess mode - need further investigation
      // if (settings) {
      //   this.setState({
      //     currentSettings: settings
      //   });
      // }

      // If a link was created, render ShareNotification view.
      if (sharingLinkCreated && (this.state.shareTargetClicked || this.props.copyLinkShortcut || this.state.applyLinkSettingsClicked)) {
        this._modifiedSharingLinkUrl = this._trackClickThru(sharingLinkCreated, settings, sharingInformation);

        const shareType = this.state.shareType;
        const currentSettings = this.state.currentSettings;
        const isEdit = currentSettings ? currentSettings.isEdit : settings.isEdit;

        const extraData: IEngagementExtraData = {
          ...this._engagementExtraData,
          shareType: shareType,
          audience: sharingLinkCreated.audience,
          isEdit: isEdit,
          recipientsCount: this.state.recipientsCount,
          externalRecipientsCount: this.state.externalRecipientsCount,
          linkedinRecipientsCount: this.state.linkedinRecipientsCount,
          hasMessage: this.state.hasMessage,
          hasSubject: this.state.hasSubject,
          messageLength: this.state.messageLength,
          daysUntilExpiry: this._getNumberOfDaysUntilExpiry(sharingLinkCreated),
          isDefaultLink: sharingInformation.defaultSharingLink.sharingLinkKind === sharingLinkCreated.sharingLinkKind
        };
        EngagementHelper.shareCompleted(extraData);

        ShareCommunicationManager.shareCompleted(this.state.shareType);

        if (shareType === ShareType.outlook || shareType === ShareType.nonOutlook) {
          /**
           * If the client ID is one that uses OWA, just pop OWA in a new window.
           * If the client ID is NOT one that uses OWA, defer to the host to take
           * action with the link created.
           */
          if (ClientIdHelper.showOWAShareTarget(this.props.clientId)) {
            // Open OWA compose.
            this._store.navigateToOwa(this._getRecipientEmails(this.state.linkRecipients), this._modifiedSharingLinkUrl);

            // Notify host that Outlook share target was clicked.
            ShareCommunicationManager.outlookClicked();

            // Dismiss share UI since Outlook is taking over.
            this._dismiss();
          } else {
            try {
              const externalJavaScript: any = window.external;
              externalJavaScript.SendViaOutlookWithContext(this._getRecipientEmails(this.state.linkRecipients), this.state.linkRecipients.length, this.state.messageText, this._modifiedSharingLinkUrl);
            } catch (error) {
              // Nothing.
            }
          }
        } else if (shareType === ShareType.teams) {
          // Dismiss share UI since Teams is taking over.
          this._dismiss();
          ShareCommunicationManager.teamsClicked(sharingLinkCreated);
        } else if (shareType === ShareType.moreApps) {
          try {
            this.setState({
              viewState: ShareViewState.linkSuccess,
              sharingLinkCreated,
              showActivityIndicator: false
            });

            const externalJavaScript: any = window.external;
            externalJavaScript.SendLinkViaMoreApps(sharingLinkCreated.url);
          } catch (error) {
            // Nothing.
          }
        } else if (this.props.mode === Mode.linkSettings) {
          ShareCommunicationManager.linkSettingsCompleted(sharingLinkCreated, companyName, this.props.customLabeledAudienceConfig);
        } else {
          this.setState({
            viewState: ShareViewState.linkSuccess,
            sharingLinkCreated,
            showActivityIndicator: false
          });
        }
      } else {
        // If we were initialized in copy link mode, and we have the sharing information, trigger the link creation
        if (this.props.copyLinkShortcut && sharingInformation && !sharingInformation.error && !this._copyLinkShortcutInvoked) {
          this._copyLinkShortcutInvoked = true;
          this._onCopyLinkClicked(true, settings);
        }

        const newState = {
          ...this.state,
          sharingInformation,
          companyName,
          groupsMemberCount,
          currentSettings: settings,
          policyTipInformation,
          isLoadingDefaultRecipients: store.isValidatingDefaultRecipients(),
          showActivityIndicator: false,
          permissionsMap
        };

        /**
         * If there are defaultRecipients in the store, and they haven't
         * yet been displayed, consume them and then send a message to
         * the store to mark it as such.
         */
        if (defaultRecipientsResponse && !defaultRecipientsResponse.isConsumed) {
          newState.linkRecipients = defaultRecipientsResponse.recipients;
          this._store.consumeDefaultRecipients();
        }

        this.setState(newState);
      }
    });

    // Make a call to get sharing information when component mounts.
    store.fetchSuiteNavInformation();
    store.fetchSharingInformation(
      this.props.copyLinkShortcut ||
      this.props.mode === Mode.linkSettings ||
      this.props.mode === Mode.manageAccess ||
      this.props.mode === Mode.notify);
    store.fetchPolicyTipInformation();
  }

  public componentDidUpdate(prevProps: IShareProps, prevState: IShareState) {
    this._resize();
  }

  public render(): React.ReactElement<{}> {
    const strings = stringsResx;

    const {
      copyLinkShortcut,
      clientId,
      showExistingAccessOption,
      createPeopleSharingLinks,
      mode,
    } = this.props;
    const {
      sharingInformation,
      currentSettings,
      showActivityIndicator,
      companyName,
      sharingLinkCreated,
      viewState,
      groupsMemberCount,
      linkRecipients
    } = this.state;

    let visibleContent;

    const maxHeightStyle = {
      maxHeight: ClientIdHelper.isMobile(this.props.clientId) ? 'none' : (this.props.maxHeight || 'none')
    };

    if (sharingInformation && sharingInformation.error) {
      // Attempt to notify host that there was an error loading the UI.
      ShareCommunicationManager.pageError(sharingInformation);

      visibleContent = (
        <div className='od-Share'>
          <Header viewState={ ShareViewState.error } />
          <div className='od-Share-error'>{ sharingInformation.errorMessage }</div>
        </div>
      );
    } else if (sharingInformation && currentSettings && !copyLinkShortcut && mode === Mode.share) {
      const hasActivityClass: string = showActivityIndicator ? ' od-Share--hasActivity' : '';

      visibleContent = (
        <div className={ 'od-Share' + hasActivityClass } style={ maxHeightStyle }>
          { this._renderBackButton() }
          { this._renderViews() }
          { this._renderActivityIndicator() }
        </div>
      );
    } else if (sharingInformation && currentSettings && copyLinkShortcut && sharingLinkCreated) {
      visibleContent = (
        <div className='od-Share' style={ maxHeightStyle }>
          <CopyLink
            clientId={ clientId }
            mode={ this.props.mode }
            scenarioId={ this.props.scenarioId }
            companyName={ companyName }
            currentSettings={ currentSettings }
            item={ sharingInformation.item }
            onLinkPermissionsApplyClicked={ this._copyLinkOnApplyClicked }
            onLinkPermissionsCancelClicked={ this._copyLinkOnCancelClicked }
            onSelectedPeopleChange={ this._onSelectedPeopleChange }
            onShareHintClicked={ this._getNotificationHintClickHandler(sharingLinkCreated.createdViaCopyLinkCommand) }
            sharingInformation={ sharingInformation }
            showExistingAccessOption={ showExistingAccessOption }
            viewState={ viewState }
            groupsMemberCount={ groupsMemberCount }
            onViewPolicyTipClicked={ this._onViewPolicyTipClicked }
            createPeopleSharingLinks={ createPeopleSharingLinks }
            displayUrl={ this._modifiedSharingLinkUrl }
            engagementExtraData={ this._engagementExtraData }
            recipients={ this.state.linkRecipients }
            isLoadingDefaultRecipients={ this.state.isLoadingDefaultRecipients }
            customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
          />
        </div>
      );
    } else if (sharingInformation && currentSettings && mode === Mode.atMention) {
      visibleContent = (
        <div>
          <AtMention
            clientId={ clientId }
            scenarioId={ this.props.scenarioId }
            currentSettings={ currentSettings }
            sharingInformation={ sharingInformation }
            recipients={ linkRecipients }
          />
        </div>
      );
    } else if (currentSettings && mode === Mode.linkSettings) {
      const hasActivityClass: string = showActivityIndicator ? ' od-Share--hasActivity' : '';
      visibleContent = (
        <div className={ 'od-Share' + hasActivityClass } style={ maxHeightStyle }>
          { this._renderModifyPermissions(ShareViewState.linkSettings) }
          { this._renderActivityIndicator() }
        </div>
      );
    } else if (currentSettings && mode === Mode.manageAccess) {
      visibleContent = (
        <div className={ 'od-Share' } style={ maxHeightStyle } >
          { this._renderPermissionsList() }
        </div>
      );
    } else if (mode === Mode.share && this.props.enableShimmer) {
      visibleContent = (
        <div className={ 'od-Share od-Share--hasShimmer' } style={ maxHeightStyle }>
          { this._renderShareMainShimmer() }
        </div>
      );
      this._viewStates.push(ShareViewState.shareShimmer);
    } else if (sharingInformation && mode === Mode.notify) {
      currentSettings.sharingLinkKind = SharingLinkKind.direct;
      currentSettings.audience = SharingAudience.existing;
      this._engagementExtraData = {
        ...this._engagementExtraData,
        appMap: sharingInformation.item.appmap
      };
      visibleContent = (
        <Notify
          clientId={ clientId }
          scenarioId={ this.props.scenarioId }
          currentSettings={ currentSettings }
          sharingInformation={ sharingInformation }
          engagementExtraData={ this._engagementExtraData }
          customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
        />);
    } else if (sharingInformation && mode === Mode.addPeople) {
      visibleContent = (
        <div className={ 'od-Share' } style={ maxHeightStyle }>
          { this._renderAddPeople() }
        </div>
      );
    } else {
      visibleContent = (
        <div className={ 'od-Share' }>
          <div className='od-Share-spinnerHolder'>
            <Spinner
              className='od-Share-spinner'
              label={ strings.componentLoading }
              size={ SpinnerSize.large }
            />
          </div>
        </div >
      );
    }

    let clientClass = ClientIdHelper.isTeams(this.props.clientId) ? ' teams' : '';
    const isForScreenReader = true;
    return (
      <Fabric>
        <span role='status' aria-live='assertive' className='od-Share-screenReaderOnly' aria-label={ this._getCurrentPageLabel(isForScreenReader) }>{ this._getCurrentPageLabel() }</span>
        <div aria-live={ !sharingInformation || sharingInformation.error ? 'assertive' : 'off' } className={ clientClass }>
          { visibleContent }
        </div>
      </Fabric>
    );
  }

  private _getRecipientEmails(recipients: Array<IPerson>): Array<string> {
    const recipientEmails = [];

    for (const recipient of recipients) {
      const email = recipient.email;
      if (email) {
        recipientEmails.push(email);
      }
    }

    return recipientEmails;
  }

  private _getNumberOfDaysUntilExpiry(sharingLink: ISharingLink) {
    const ONE_DAY_IN_MS = 86400000;
    const expiration = sharingLink.expiration && new Date(sharingLink.expiration as any);
    const today = new Date();

    if (!expiration) {
      return undefined;
    }

    const numberOfDays = (expiration.getTime() - today.getTime()) / ONE_DAY_IN_MS;
    return Math.floor(numberOfDays);
  }

  @autobind
  private _copyLinkOnCancelClicked() {
    this.setState({
      viewState: ShareViewState.linkSuccess
    }, () => {
      EngagementHelper.linkSettingsCancelClicked(this._engagementExtraData);
    });
  }

  @autobind
  private _copyLinkOnApplyClicked(settings: ISharingLinkSettings) {
    this.setState({
      currentSettings: settings,
      linkRecipients: settings.specificPeople
    }, () => {
      EngagementHelper.linkSettingsApplyClicked(this._engagementExtraData);
      this._onCopyLinkClicked(true);
    });
  }

  @autobind
  private _closeButtonOnBlur (): void {
    this.setState({
      isCloseButtonOnFocus: false
    });
  }

  @autobind
  private _closeButtonOnFocus (): void {
    this.setState({
      isCloseButtonOnFocus: true
    });
  }

  private _handleEscKeyPress(event: KeyboardEvent): void {
    const viewState = this.state.viewState;
    const isModifyPermissionsView = (viewState === ShareViewState.modifyPermissions);
    const isPermissionsListView = (viewState === ShareViewState.permissionsList);
    const isShareMode = (this.props.mode === Mode.share);
    if (isShareMode && isModifyPermissionsView) {
      this._onLinkPermissionsCancelClicked();
    } else if (isShareMode && isPermissionsListView) {
      this._onBackButtonClicked();
    } else {
      this._dismiss();
    }
    event.stopPropagation();
  }

  private _initializeCurrentSettings(link: ISharingLink, sharingInformation: ISharingInformation, isCopyLink: boolean): ISharingLinkSettings {
    /**
     * If we're in "copy" mode and access requests are required,
     * default to "People with existing access".
     */
    if (sharingInformation.requiresAccessApproval && isCopyLink) {
      return {
        allowEditing: true,
        audience: SharingAudience.existing,
        expiration: null,
        isEdit: true,
        sharingLinkKind: SharingLinkKind.direct,
        specificPeople: [],
        updatePassword: false,
        password: "",
        preventDownload: false,
        shareId: ''
      };
    }
    /**
     * Short-circuit and use sharing abilitites to initialize setting
     * if flight is on.
     */
    if (sharingInformation.defaultSharingLink === link && this.context.usesSharingAbilities && sharingInformation.sharingAbilities) {
      return this._initializeCurrentSettingsWithSharingAbilities(link, sharingInformation, isCopyLink);
    }

    /**
     * If the default is "Specific people", ensure that the user
     * has permissions to share. If they don't, make the default
     * "People with existing access".
     *
     * If the default is "Specific people" and the user does have
     * permissions to share, check to see if PSLs are enabled. If
     * they are, short-circuit the function and set the default
     * to PSL.
     *
     * If the default is "Specific people", the mode is LinkSettings
     * and the clientId is outlookWeb, make the default
     * "People with existing access" until their scenario supports it.
     */
    if (link.audience === SharingAudience.specificPeople) {
      const isOutlookWebLinkSettings = this.props.mode === Mode.linkSettings && this.props.clientId === ClientId.outlookWeb;
      if (!sharingInformation.canManagePermissions || sharingInformation.blockPeoplePickerAndSharing || isOutlookWebLinkSettings) {
        link.audience = SharingAudience.existing;
      } else if (this.props.createPeopleSharingLinks) {
        return {
          allowEditing: true,
          audience: SharingAudience.specificPeopleFlex,
          expiration: null,
          isEdit: true,
          sharingLinkKind: SharingLinkKind.flexible,
          specificPeople: [],
          updatePassword: false,
          password: "",
          preventDownload: false,
          shareId: ''
        };
      }
    }

    /**
     * If the site collection's default link is "direct" (and PSLs are enabled),
     * instead default to "existing".
     */
    if (link.audience === SharingAudience.specificPeopleFlex && isCopyLink) {
      link.audience = SharingAudience.existing;
      link.sharingLinkKind = SharingLinkKind.direct;
    }

    const sharingLinkKind = link.sharingLinkKind;
    const allowEditing = sharingLinkKind === SharingLinkKind.anonymousEdit || sharingLinkKind === SharingLinkKind.organizationEdit || sharingLinkKind === SharingLinkKind.direct || sharingLinkKind === SharingLinkKind.flexible;

    let specificPeople = [];
    if (this.props.shareId && link.principals) {
      specificPeople = getLinkParticipants(link);
    }

    return {
      allowEditing: allowEditing,
      audience: link.audience,
      expiration: link.expiration || null,
      isEdit: link.isEdit && sharingInformation.defaultShareLinkPermission === SharePermission.edit,
      sharingLinkKind: link.sharingLinkKind,
      specificPeople: specificPeople,
      updatePassword: false,
      password: "",
      preventDownload: false,
      shareId: ''
    };
  }

  private _initializeCurrentSettingsWithSharingAbilities(link: ISharingLink, sharingInformation: ISharingInformation, isCopyLink: boolean): ISharingLinkSettings {
    const sharingAbilities = sharingInformation.sharingAbilities;
    const suggestedDirectLink = this.props.shareId === 'direct';
    let defaultLinkKind = suggestedDirectLink ? SharingLinkKind.direct : sharingInformation.defaultSharingLink.sharingLinkKind;

    // Determine audience based on defaultLinkKind.
    const hasRecipients = this._store.isValidatingDefaultRecipients() || this.state.linkRecipients.length > 0;
    let audience = isCustomLabeledSpecificPeopleSupported(this.props.clientId, this.props.customLabeledAudienceConfig, hasRecipients) ?
      SharingAudience.customLabeledSpecificPeople :
      SharingAudience.specificPeople;
    // let audience = SharingAudience.specificPeople;
    switch (defaultLinkKind) {
      case SharingLinkKind.anonymousEdit:
      case SharingLinkKind.anonymousView:
        audience = SharingAudience.anyone;
        break;
      case SharingLinkKind.organizationEdit:
      case SharingLinkKind.organizationView:
        audience = SharingAudience.organization;
        break;
      case SharingLinkKind.direct:
      default:
        if (suggestedDirectLink || !sharingInformation.canManagePermissions || sharingInformation.blockPeoplePickerAndSharing) {
          audience = SharingAudience.existing;
        } else if (this.props.createPeopleSharingLinks) {
          if (sharingAbilities.peopleSharingLinkAbilities.canManageEditLink.enabled || sharingAbilities.peopleSharingLinkAbilities.canManageReadLink.enabled) {
            audience = SharingAudience.specificPeopleFlex;
          } else {
            audience = SharingAudience.existing;
          }
        }
        break;
    }

    /**
     * If the site collection's default link is "direct" (and PSLs are enabled),
     * instead default to "existing".
     */
    if (audience === SharingAudience.specificPeopleFlex && isCopyLink) {
      audience = SharingAudience.existing;
      defaultLinkKind = SharingLinkKind.direct;
    }

    if (audience === SharingAudience.customLabeledSpecificPeople) {
      defaultLinkKind = SharingLinkKind.flexible;
    }

    // Get abilities for given audience.
    const abilities = SharingAbilitiesHelper.getSharingAbilitiesByAudience(audience, sharingAbilities);

    // Get link expiration value.
    let expiration = null;
    const linkExpirationAbilities = abilities.linkExpiration;
    const defaultExpirationInDays = linkExpirationAbilities ? linkExpirationAbilities.defaultExpirationInDays : null;
    if (defaultExpirationInDays > 0) {
      expiration = new Date((new Date().toDateString()));
      expiration.setDate(expiration.getDate() + linkExpirationAbilities.defaultExpirationInDays);
    }

    // Determine if editing is allowed.
    const allowEditing = SharingAbilitiesHelper.isEditingAllowed(audience, sharingAbilities);

    return {
      allowEditing: allowEditing,
      audience: audience,
      expiration: expiration,
      isEdit: allowEditing && sharingInformation.defaultShareLinkPermission === SharePermission.edit,
      sharingLinkKind: defaultLinkKind,
      specificPeople: [],
      updatePassword: false,
      password: "",
      shareId: ''
    };
  }

  @autobind
  private _loadTheme(theme: IPartialTheme) {
    try {
      const partialTheme: IPartialTheme = theme;
      loadTheme(partialTheme);
    } catch (e) {
      // invalid theme passed
    }
  }

  @autobind
  private _onShareTargetClicked(shareType: ShareType): void {
    if (shareType === ShareType.copy) {
      ShareCommunicationManager.copyLinkShareTargetClicked();
      this._onCopyLinkClicked();
    } else if (shareType === ShareType.outlook) {
      this._onOutlookClicked();
    } else if (shareType === ShareType.nonOutlook) {
      this._onNonOutlookClicked();
    } else if (shareType === ShareType.teams) {
      this._onTeamsClicked();
    } else if (shareType === ShareType.moreApps) {
      this._onMoreAppsClicked();
    }
  }

  /**
   * Creates a link to copy.
   * @param initializedSettings Only used in the initial click of the "Copy link" command.
   */
  @autobind
  private _onCopyLinkClicked(copyLinkShortcut?: boolean, initializedSettings?: ISharingLinkSettings): void {
    const settings = {
      ...initializedSettings,
      ...this.state.currentSettings
    };

    this.setState({
      shareType: ShareType.copy,
      shareTargetClicked: true
    }, () => {
      this._store.shareLink({
        settings: settings,
        recipients: this.state.linkRecipients,
        copyLinkCommand: copyLinkShortcut
      });
    });
  }

  @autobind
  private _onOutlookClicked(): void {
    this.setState({
      shareType: ShareType.outlook,
      shareTargetClicked: true
    }, () => {
      this._store.shareLink({
        settings: this.state.currentSettings,
        recipients: this.state.linkRecipients,
        copyLinkCommand: false
      });
    });
  }

  @autobind
  private _onNonOutlookClicked(): void {
    this.setState({
      shareType: ShareType.nonOutlook,
      shareTargetClicked: true
    }, () => {
      this._store.shareLink({
        settings: this.state.currentSettings,
        recipients: this.state.linkRecipients,
        copyLinkCommand: false
      });
    });
  }

  @autobind
  private _onTeamsClicked(): void {
    this.setState({
      shareType: ShareType.teams,
      shareTargetClicked: true
    }, () => {
      this._store.shareLink({
        settings: this.state.currentSettings,
        recipients: this.state.linkRecipients,
        copyLinkCommand: false
      });
    });
  }

  @autobind
  private _onMoreAppsClicked(): void {
    this.setState({
      shareType: ShareType.moreApps,
      shareTargetClicked: true
    }, () => {
      this._store.shareLink({
        settings: this.state.currentSettings,
        recipients: this.state.linkRecipients,
        copyLinkCommand: false
      });
    });
  }

  @autobind
  private _onLinkPermissionsApplyClicked(newSettings: ISharingLinkSettings): void {
    EngagementHelper.linkSettingsApplyClicked(this._engagementExtraData);

    this._onSelectedPermissionsChange(newSettings);

    if (this.props.mode === Mode.linkSettings) {
      this.setState({
        shareType: ShareType.share,
        applyLinkSettingsClicked: true
      }, () => {
        this._store.shareLink({
          settings: this.state.currentSettings,
          recipients: this.state.linkRecipients,
          copyLinkCommand: false
        });
      });
    }
  }

  @autobind
  private _onLinkPermissionsCancelClicked(): void {
    if (this.props.mode === Mode.linkSettings) {
      ShareCommunicationManager.dismiss();
    }
    this.setState({
      viewState: ShareViewState.default
    }, () => {
      EngagementHelper.linkSettingsCancelClicked(this._engagementExtraData);
    });
  }

  @autobind
  private _onBackButtonClicked(): void {
    this.setState({ viewState: this._viewStates.pop() });
  }

  @autobind
  private _onLinkPermissionsReady(): void {
    if (this.props.mode === Mode.linkSettings) {
      // Attempt to notify host that UI is really ready.
      ShareCommunicationManager.pageFinishedLoading(this.props.clientId, this.props.scenarioId);
    }
  }

  @autobind
  private _onSelectedPermissionsChange(permissions: ISharingLinkSettings) {
    // If new audience is "existing", check permissions of already resolved users.
    let permissionsMap = this.state.permissionsMap;
    if (permissions.audience === SharingAudience.existing) {
      this._store.checkPermissions(this.state.linkRecipients);
    } else {
      permissionsMap = {};
    }

    this.setState({
      viewState: ShareViewState.default,
      currentSettings: {
        allowEditing: permissions.allowEditing,
        audience: permissions.audience,
        expiration: permissions.expiration,
        isEdit: permissions.isEdit,
        sharingRole: permissions.sharingRole,
        sharingLinkKind: permissions.sharingLinkKind,
        specificPeople: permissions.specificPeople,
        updatePassword: permissions.updatePassword,
        password: permissions.password,
        preventDownload: permissions.preventDownload,
        shareId: permissions.shareId
      },
      permissionsMap: permissionsMap
    });
  }

  @autobind
  private _onSelectedPeopleChange(items: Array<IPerson>) {
    const permissionsMap = this.state.permissionsMap;
    if (this.state.currentSettings.audience === SharingAudience.existing) {
      // Get new permissions map from the store.
      this._store.checkPermissions(items);
    }

    this.setState({
      linkRecipients: items,
      permissionsMap
    }, () => {
      EngagementHelper.peoplePickerLinkedinSuggestionSelected(this._engagementExtraData, items);
      this._store.fetchGroupsMemberCount(items);
    });
  }

  @autobind
  private _onSendLinkClicked(message: string, subject?: string): void {
    const recipients = this.state.linkRecipients;

    this.setState({
      shareType: ShareType.share,
      recipientsCount: recipients.length,
      externalRecipientsCount: PeoplePickerHelper.getExternalPeopleCount(recipients),
      linkedinRecipientsCount: PeoplePickerHelper.getLinkedInPeopleCount(recipients),
      hasMessage: !!message,
      hasSubject: !!subject,
      messageLength: (message ? message.length : 0),
      shareTargetClicked: true
    }, () => {
      ShareCommunicationManager.sendLinkClicked();
      this._store.shareLink({
        settings: this.state.currentSettings,
        recipients: recipients,
        emailMessage: message,
        emailSubject: subject
      });
    });
  }

  @autobind
  private _onViewPolicyTipClicked(): void {
    this.setState({
      viewState: ShareViewState.policyDetails
    });
  }

  @autobind
  private _onSendLinkMessageChange(messageText: string): void {
    this.setState({
      messageText
    });
  }

  @autobind
  private _onSendLinkSubjectChange(subjectText: string): void {
    this.setState({
      subjectText
    });
  }

  @autobind _onShareTargetsRendered(shareTargets: Array<ShareType>): void {
    const extraData: IEngagementExtraData = {
      ...this._engagementExtraData,
      shareTargets
    };

    EngagementHelper.shareTargets(extraData);
  }

  private _renderBackButton(): JSX.Element {
    const viewState = this.state.viewState;

    if (viewState === ShareViewState.policyDetails) {
      return (
        <IconButton
          className='od-Share-backButton'
          onClick={ this._onBackButtonClicked }
          iconProps={ { iconName: 'Back' } }
        >
          <label className='od-Share-screenReaderOnly'>{ stringsResx.backButtonLabel }</label>
        </IconButton>
      );
    }
  }

  @autobind
  private _renderViews(): JSX.Element {
    switch (this.state.viewState) {
      case ShareViewState.modifyPermissions:
        return this._renderModifyPermissions(ShareViewState.modifyPermissions);
      case ShareViewState.linkSettings:
        return this._renderModifyPermissions(ShareViewState.linkSettings);
      case ShareViewState.linkSuccess:
        return this._renderNotification();
      case ShareViewState.permissionsList:
        return this._renderPermissionsList();
      case ShareViewState.policyDetails:
        return this._renderPolicyDetails();
      default:
        return this._renderShareMain(this.state.sharingInformation, this.state.currentSettings);
    }
  }

  @autobind
  private _renderShareMain(sharingInformation: ISharingInformation, settings: ISharingLinkSettings): React.ReactElement<{}> {
    this._viewStates.push(ShareViewState.default);

    return (
      <ShareMain
        key='share'
        clientId={ this.props.clientId }
        scenarioId={ this.props.scenarioId }
        companyName={ this.state.companyName }
        currentSettings={ settings }
        item={ sharingInformation.item }
        onPolicyClick={ this._showPolicy }
        onSelectedPeopleChange={ this._onSelectedPeopleChange }
        onSendLinkClicked={ this._onSendLinkClicked }
        onShareHintClicked={ this._showModifyPermissions }
        onShowPermissionsListClicked={ this._showPermissionsList }
        sharingInformation={ sharingInformation }
        groupsMemberCount={ this.state.groupsMemberCount }
        onViewPolicyTipClicked={ this._onViewPolicyTipClicked }
        linkRecipients={ this.state.linkRecipients }
        permissionsMap={ this.state.permissionsMap }
        messageText={ this.state.messageText }
        subjectText={ this.state.subjectText }
        onSendLinkMessageChange={ this._onSendLinkMessageChange }
        onSendLinkSubjectChange={ this._onSendLinkSubjectChange }
        onShareTargetClicked={ this._onShareTargetClicked }
        onShareTargetsRendered={ this._onShareTargetsRendered }
        enableShimmer={ this.props.enableShimmer }
        hideShareDialogOverflowButton={ this.props.hideShareDialogOverflowButton }
        createPeopleSharingLinks={ this.props.createPeopleSharingLinks }
        outlookAvailability={ this._outlookAvailability }
        engagementExtraData={ this._engagementExtraData }
        isLoadingDefaultRecipients={ this.state.isLoadingDefaultRecipients }
        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
        closeButtonOnFocus={ this._closeButtonOnFocus }
        closeButtonOnBlur={ this._closeButtonOnBlur }
      />
    );
  }

  private _renderPolicyDetails(): JSX.Element {
    return (
      <SharePolicyDetails
        clientId={ this.props.clientId }
        sharingInformation={ this.state.sharingInformation }
        policyTipInformation={ this.state.policyTipInformation }
      />
    );
  }

  @autobind
  private _renderModifyPermissions(state: ShareViewState): JSX.Element {
    this._viewStates.push(state);

    return (
      <ModifyPermissions
        clientId={ this.props.clientId }
        mode={ this.props.mode }
        companyName={ this.state.companyName }
        currentSettings={ this.state.currentSettings }
        doesCreate={ true }
        onCancel={ this._onLinkPermissionsCancelClicked }
        onReady={ this._onLinkPermissionsReady }
        onSelectedPermissionsChange={ this._onLinkPermissionsApplyClicked }
        onSelectedPeopleChange={ this._onSelectedPeopleChange }
        sharingInformation={ this.state.sharingInformation }
        showExistingAccessOption={ true }
        groupsMemberCount={ this.state.groupsMemberCount }
        onViewPolicyTipClicked={ this._onViewPolicyTipClicked }
        createPeopleSharingLinks={ this.props.createPeopleSharingLinks }
        copyLinkShortcut={ false }
        engagementExtraData={ this._engagementExtraData }
        recipients={ this.state.linkRecipients }
        isLoadingDefaultRecipients={ this.state.isLoadingDefaultRecipients }
        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
      />
    );
  }

  @autobind
  private _renderNotification(): JSX.Element {
    const state = this.state;
    const sharingInformation = state.sharingInformation;
    const sharingLinkCreated = state.sharingLinkCreated;

    const sharingHintProps = {
      companyName: state.companyName,
      currentSettings: state.currentSettings,
      sharingInformation: sharingInformation,
      onShareHintClick: this._getNotificationHintClickHandler(sharingLinkCreated.createdViaCopyLinkCommand),
      linkRecipients: state.shareType === ShareType.share ? this.state.linkRecipients : null,
      customLabeledAudienceConfig: this.props.customLabeledAudienceConfig
    };

    return (
      <ShareNotification
        shareType={ state.shareType }
        itemName={ sharingInformation.item.name }
        linkUrl={ this._modifiedSharingLinkUrl }
        clientId={ this.props.clientId }
        sharingHintProps={ sharingHintProps }
        shouldCopyLinkToClipboard={ true }
        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
      />
    );
  }

  private _getNotificationHintClickHandler(createdViaCopyLink: boolean) {
    return createdViaCopyLink ? this._showModifyPermissions.bind(this, createdViaCopyLink) : null;
  }

  @autobind
  private _renderShareMainShimmer(): JSX.Element {
    const settings: ISharingLinkSettings = {
      allowEditing: false,
      audience: SharingAudience.existing,
      expiration: undefined,
      isEdit: false,
      sharingLinkKind: SharingLinkKind.uninitialized,
      specificPeople: [],
      updatePassword: false,
      password: "",
      preventDownload: false,
      shareId: ''
    };
    const sharingInformation: ISharingInformation = {
      anonymousLinkExpirationRestrictionDays: 0,
      blockPeoplePickerAndSharing: true,
      canAddExternalPrincipal: false,
      canAddInternalPrincipal: false,
      canManagePermissions: false,
      defaultSharingLink: undefined,
      isShared: false,
      item: this.props.sharingItemInformation ? this.props.sharingItemInformation : {
        name: '',
        childCount: 0,
        hasDlpPolicy: false
      },
      sharingLinks: [],
      sharingPrincipals: [],
      peoplePickerSettings: {},
      userDisplayName: '',
      defaultShareLinkPermission: SharePermission.view,
      defaultLinkKind: SharingLinkKind.uninitialized,
      userRole: SharingRole.none,
      hasPermissionsInformation: false,
      sharingAbilities: null,
      directLink: null,
      sharingLinkTemplates: null,
      webTemplateId: undefined
    };

    return this._renderShareMain(sharingInformation, settings);
  }

  @autobind
  private _renderPermissionsList(): JSX.Element {
    if (!this.state.sharingInformation || !this.state.sharingInformation.hasPermissionsInformation) {
      this._store.fetchSharingInformation(true);
    }
    return (
      <PermissionsList
        clientId={ this.props.clientId }
        scenarioId={ this.props.scenarioId }
        mode={ this.props.mode }
        companyName={ this.state.companyName }
        sharingInformation={ this.state.sharingInformation }
        linkRecipients={ this.state.linkRecipients }
        groupsMemberCount={ this.state.groupsMemberCount }
        viewState={ this.state.viewState }
        backButtonClicked={ this._onBackButtonClicked }
      />
    );
  }

  @autobind
  private _renderAddPeople(): JSX.Element {
    return (
      <AddPeople
        sharingInformation={ this.state.sharingInformation }
        clientId={ this.props.clientId }
      />
    );
  }

  @autobind
  private _showModifyPermissions(createdViaCopyLink?: boolean): void {
    this.setState({
      viewState: ShareViewState.modifyPermissions
    }, () => {
      EngagementHelper.linkSettingsOpened(this._engagementExtraData);
    });
  }

  @autobind
  private _showPermissionsList(): void {
    EngagementHelper.manageAccessOpened(this._engagementExtraData);

    const onManageAccessClicked = this.props.onManageAccessClicked;

    if (onManageAccessClicked) {
      onManageAccessClicked();
    } else {
      this.setState({
        viewState: ShareViewState.permissionsList
      });
    }
  }

  @autobind
  private _showPolicy(): void {
    this.setState({
      viewState: ShareViewState.policyDetails
    });
  }

  private _renderActivityIndicator(): React.ReactElement<{}> {
    if (this.state.showActivityIndicator) {
      return (
        <ActivityIndicator message={ stringsResx.applyingLinkSettings } />
      );
    }
  }

  private _getCurrentPageLabel(isForScreenReader: boolean = false): string {
    const strings = stringsResx;
    const { isCloseButtonOnFocus } = this.state;
    const defaultPageLabelSendLink = (isForScreenReader && this._platformDetection.isAndroid && isCloseButtonOnFocus) ?
          `${strings.pageLabelSendLink} ${strings.focusCloseButton}` : strings.pageLabelSendLink;

    const viewStringMap = {
      [ShareViewState.modifyPermissions]: strings.pageLabelLinkSettings,
      [ShareViewState.linkSuccess]: strings.pageLabelLinkCreated,
      [ShareViewState.permissionsList]: strings.pageLabelManageAccess,
      [ShareViewState.policyDetails]: strings.pageLabelPolicyTipDetails,
      [ShareViewState.default]: defaultPageLabelSendLink
    };

    // Determine label based on view state if in share/copy mode.
    let currentPageLabel = viewStringMap[this.state.viewState] || '';

    // Ignore view state if in a non-share/copy mode.
    if (this.props.mode === Mode.atMention) {
      currentPageLabel = strings.grantAccessPageLabel;
    }

    // Ignore view state if in manage access mode.
    if (this.props.mode === Mode.manageAccess) {
      currentPageLabel = strings.pageLabelManageAccess;
    }

    // Add label for spinner if it's shown.
    if (this.state.showActivityIndicator) {
      currentPageLabel = strings.applyingLinkSettings + currentPageLabel;
    }

    return currentPageLabel;
  }

  @autobind
  private _trackClickThru(link: ISharingLink, settings: ISharingLinkSettings, sharingInformation: ISharingInformation): string {
    const shareType = this.state.shareType;
    let url = link.url;

    /**
     * Currently only tracking links created via "Copy" and "Outlook" share targets,
     * so just return the given URL if it's not one of those.
     */
    if (shareType !== ShareType.copy && shareType !== ShareType.outlook) {
      return url;
    }

    // Get base62 ID.
    const base62Id = this._getBase62Id();

    // Fire engagement event that an ID was created for this link.
    this._logClickThru(base62Id, link, settings, sharingInformation);

    // Append GUID and return.
    const uri = new Uri(url);
    uri.setQueryParameter('e', base62Id);
    return uri.toString();
  }

  @autobind
  private _logClickThru(clickThruId: string, link: ISharingLink, settings: ISharingLinkSettings, sharingInformation: ISharingInformation): void {
    const state = this.state;

    const extraData: IEngagementExtraData = {
      ...this._engagementExtraData,
      shareType: state.shareType,
      audience: link.audience,
      isEdit: settings.isEdit,
      recipientsCount: this.state.recipientsCount,
      externalRecipientsCount: this.state.externalRecipientsCount,
      linkedinRecipientsCount: this.state.linkedinRecipientsCount,
      hasMessage: this.state.hasMessage,
      hasSubject: this.state.hasSubject,
      messageLength: this.state.messageLength,
      daysUntilExpiry: this._getNumberOfDaysUntilExpiry(link),
      isDefaultLink: sharingInformation.defaultSharingLink.sharingLinkKind === link.sharingLinkKind,
      clickThruId: clickThruId,
      clickThruScenario: ShareType[state.shareType]
    };

    EngagementHelper.trackClickThru(extraData);
  }

  /**
   * Helper function to generate a base62 value to append to sharing links
   * to track click-thru rates.
   */
  private _getBase62Id() {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    const buffer = new Uint8Array(6);
    // Fall back to prefixed for IE11
    const cryptoLib = window.crypto || window.msCrypto;
    cryptoLib.getRandomValues(buffer);

    const key = [];
    for (let i = 0; i < buffer.length; i++) {
      key.push(chars[buffer[i] % 62]);
    }

    return key.join('');
  }
}
